import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { v4 } from "uuid";

import type { AgentMessage, MessagePart } from "@citrine/resources/agents";
import { AgentsClient, isDebugEvent } from "@citrine/resources/agents";
import { useConfiguration } from "@citrine/resources/configuration";

import {
  invalidateForTool,
  parseToolCompleteEvent,
} from "./tool-invalidations";

/** Concatenated text of an assistant message (text parts only, ignoring chips). */
export function getMessageText(message: AgentMessage): string {
  return message.parts
    .map((part) => (part.kind === "text" ? part.text : ""))
    .join("");
}

/** Append a token to the last assistant text part, or start a new one if the last part is a chip. */
function appendToken(messages: AgentMessage[], text: string): AgentMessage[] {
  const lastMessage = messages.at(-1);
  if (lastMessage?.role !== "assistant") {
    return messages;
  }

  const lastPart = lastMessage.parts.at(-1);
  const trailingTextPart = lastPart?.kind === "text" ? lastPart : null;

  const nextParts = [...lastMessage.parts];
  if (trailingTextPart) {
    // Replace the last part, preserving its id so the React key stays stable
    // across tokens (a fresh id per token would remount the markdown each time).
    nextParts[nextParts.length - 1] = {
      id: trailingTextPart.id,
      kind: "text",
      text: trailingTextPart.text + text,
    };
  } else {
    nextParts.push({ id: v4(), kind: "text", text });
  }

  const nextMessages = [...messages];
  nextMessages[nextMessages.length - 1] = { ...lastMessage, parts: nextParts };
  return nextMessages;
}

/** Append an event/error chip as a new part on the last assistant message. */
function appendPart(
  messages: AgentMessage[],
  part: Exclude<MessagePart, { kind: "text" }>,
): AgentMessage[] {
  const lastMessage = messages.at(-1);
  if (lastMessage?.role !== "assistant") {
    return messages;
  }

  const nextMessages = [...messages];
  nextMessages[nextMessages.length - 1] = {
    ...lastMessage,
    parts: [...lastMessage.parts, part],
  };
  return nextMessages;
}

export function useAgentStream(agentKey: string) {
  // When false, hides runtime diagnostics (heartbeats, thinking markers, token
  // refreshes). Tool-call events and errors always show.
  const enableCatalystAgentDebug = useConfiguration("enableCatalystAgentDebug");
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);

  const abortRef = useRef<AbortController | null>(null);

  const { mutateAsync: sendMessage } =
    AgentsClient.getInstance().useSendMessage();

  const handleSendMessage = useCallback(
    async (prompt: string, context?: object) => {
      // The runtime is stateless; send the full history (prior turns + new
      // user turn) each call.
      const outgoing: AgentMessage[] = [
        ...messages,
        {
          id: v4(),
          role: "user",
          parts: [{ id: v4(), kind: "text", text: prompt }],
        },
      ];

      setMessages([...outgoing, { id: v4(), role: "assistant", parts: [] }]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        await sendMessage({
          abortSignal: controller.signal,
          agentKey,
          context,
          messages: outgoing.map((message) => ({
            role: message.role,
            content: getMessageText(message),
          })),
          onEvent: (event) => {
            const id = v4();
            switch (event.type) {
              case "error":
                setMessages((prev) =>
                  appendPart(prev, { id, kind: "error", text: event.data }),
                );
                break;
              case "event": {
                // Invalidate caches on tool completion regardless of the debug filter.
                const completedTool = parseToolCompleteEvent(event.data);
                if (completedTool) {
                  invalidateForTool(queryClient, completedTool);
                }

                if (!enableCatalystAgentDebug && isDebugEvent(event.data)) {
                  break;
                }

                setMessages((prev) =>
                  appendPart(prev, { id, kind: "event", text: event.data }),
                );
                break;
              }
              case "token":
              default:
                setMessages((prev) => appendToken(prev, event.data));
            }
          },
        });
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          return;
        }

        const message = `Error: ${error instanceof Error ? error.message : "unknown"}`;
        setMessages((prev) =>
          appendPart(prev, { id: v4(), kind: "error", text: message }),
        );
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [agentKey, enableCatalystAgentDebug, queryClient, messages, sendMessage],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsStreaming(false);
  }, []);

  return { messages, isStreaming, sendMessage: handleSendMessage, reset };
}
